import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  MenuItem,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DescribedField from '../DescribedField';

const emptyItem = () => ({
  id: crypto.randomUUID(),
  placeId: '',
  showMap: false,
});

/**
 * LocationsBlockEditor — Editor do bloco Locais (só company)
 * TODO 4.10
 *
 * Props:
 *   - value: { items: [{ id, placeId, showMap }] }
 *   - onChange: (patch) => void
 *   - context: { availablePlaces }
 */
const LocationsBlockEditor = ({ value = {}, onChange, context = {} }) => {
  const { items = [] } = value;
  const { availablePlaces = [] } = context;

  const patchItems = (newItems) => onChange({ ...value, items: newItems });

  const addItem = () => patchItems([...items, emptyItem()]);

  const removeItem = (index) => patchItems(items.filter((_, i) => i !== index));

  const updateItem = (index, field, val) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [field]: val } : item
    );
    patchItems(updated);
  };

  // Ao selecionar o local, faz SNAPSHOT de nome/endereço/telefone no config do bloco.
  // O template é HTML estático e não resolve placeId → precisa dos dados denormalizados
  // (mesmo padrão do bloco Serviços/Equipe). placeId fica guardado só como referência.
  // Limpar a seleção zera o snapshot.
  const selectPlace = (index, placeId) => {
    const place = availablePlaces.find((p) => p.id === placeId);
    const updated = items.map((item, i) =>
      i === index
        ? {
            ...item,
            placeId,
            name: place?.name ?? '',
            address: place?.address ?? '',
            phone: place?.phone ?? '',
          }
        : item
    );
    patchItems(updated);
  };

  return (
    <DescribedField
      label="Locais de atendimento"
      description="Cartões de endereço dos locais envolvidos na página. Traz nome, endereço completo, telefone e mapa opcional. Nome e endereço vêm do cadastro de Locais — para alterar, edite o local lá. Essencial para páginas multilocais."
      helperLink={{ entity: 'place' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((item, index) => (
          <Paper key={item.id ?? index} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Local {index + 1}</Typography>
              <IconButton
                size="small"
                onClick={() => removeItem(index)}
                aria-label="Remover local"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              select
              fullWidth
              label="Local"
              value={item.placeId}
              onChange={(e) => selectPlace(index, e.target.value)}
              size="small"
              sx={{ mb: 1.5 }}
              helperText="Local de atendimento. Nome, endereço e telefone são copiados do cadastro ao selecionar."
            >
              <MenuItem value="">-- Selecione --</MenuItem>
              {availablePlaces.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name || p.id}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={item.showMap}
                  onChange={(e) => updateItem(index, 'showMap', e.target.checked)}
                />
              }
              label="Mostrar mapa"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Se ligado, mostra um mapa estático com o endereço. Desligado exibe só o texto.
            </Typography>
          </Paper>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={addItem}
          variant="outlined"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        >
          Adicionar local
        </Button>
      </Box>
    </DescribedField>
  );
};

LocationsBlockEditor.propTypes = {
  value: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        placeId: PropTypes.string,
        name: PropTypes.string,     // snapshot do cadastro
        address: PropTypes.string,  // snapshot do cadastro
        phone: PropTypes.string,    // snapshot do cadastro
        showMap: PropTypes.bool,
      })
    ),
  }),
  onChange: PropTypes.func.isRequired,
  context: PropTypes.shape({
    availablePlaces: PropTypes.array,
  }),
};

export default LocationsBlockEditor;
